import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { RECORDER_STATE } from "./constants";
import useAudioRecorder from "./useAudioRecorder";

function useEventCallback(handler) {
    const handlerRef = useRef(handler);

    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);

    return useCallback((...args) => handlerRef.current?.(...args), []);
}

const RecorderStableContext = createContext({
    activeTreatmentId: null,
    clinicalNoteSettings: null,
    closeSummarySettingsModal: () => { },
    dockedRect: null,
    ensureRecorderStarted: async () => { },
    handleContinueRecording: async () => { },
    handlePauseOrResumeRecording: async () => { },
    handleRestartRecording: async () => { },
    handleStopRecording: async () => { },
    handleSummarizeAgain: async () => { },
    isRecorderVisible: false,
    isSummarySettingsModalEditMode: false,
    isSummarySettingsModalOpen: false,
    latestSummaryResult: null,
    openSummarySettingsModal: () => { },
    recorderState: RECORDER_STATE.IDLE,
    resetRecorder: async () => { },
    startFreshRecorderSession: async () => { },
    summarizeBySettings: async () => { },
    updateDockedRect: () => { },
});

const RecorderLiveContext = createContext({
    audioIntensity: "silent",
    audioLevel: 0,
    elapsedSeconds: 0,
    recorderErrorMessage: "",
    recorderStatusMeta: {
        color: "oslo",
        text: "",
    },
});

export function RecorderProvider({ children }) {
    const recorder = useAudioRecorder();
    const [dockedRect, setDockedRect] = useState(null);
    const [summarySettingsModalState, setSummarySettingsModalState] = useState({
        editMode: false,
        isOpen: false,
    });

    const updateDockedRect = useCallback((nextRect) => {
        if (!nextRect) {
            return;
        }

        setDockedRect((currentRect) => {
            if (
                currentRect
                && currentRect.x === nextRect.x
                && currentRect.y === nextRect.y
                && currentRect.width === nextRect.width
                && currentRect.height === nextRect.height
            ) {
                return currentRect;
            }

            return nextRect;
        });
    }, []);

    const openSummarySettingsModal = useCallback((editMode = false) => {
        setSummarySettingsModalState({
            editMode,
            isOpen: true,
        });
    }, []);

    const closeSummarySettingsModal = useCallback(() => {
        setSummarySettingsModalState((currentState) => {
            if (!currentState.isOpen) {
                return currentState;
            }

            return {
                ...currentState,
                isOpen: false,
            };
        });
    }, []);

    const {
        activeTreatmentId,
        audioIntensity,
        audioLevel,
        clinicalNoteSettings,
        elapsedSeconds,
        recorderErrorMessage,
        ensureRecorderStarted,
        handleContinueRecording,
        handlePauseOrResumeRecording,
        handleRestartRecording,
        handleStopRecording,
        handleSummarizeAgain,
        isRecorderVisible,
        latestSummaryResult,
        recorderState,
        recorderStatusMeta,
        resetRecorder,
        startFreshRecorderSession,
        summarizeBySettings,
    } = recorder;

    const ensureRecorderStartedStable = useEventCallback(ensureRecorderStarted);
    const handleContinueRecordingStable = useEventCallback(handleContinueRecording);
    const handlePauseOrResumeRecordingStable = useEventCallback(handlePauseOrResumeRecording);
    const handleRestartRecordingStable = useEventCallback(handleRestartRecording);
    const handleStopRecordingStable = useEventCallback(handleStopRecording);
    const handleSummarizeAgainStable = useEventCallback(handleSummarizeAgain);
    const resetRecorderStable = useEventCallback(resetRecorder);
    const startFreshRecorderSessionStable = useEventCallback(startFreshRecorderSession);
    const summarizeBySettingsStable = useEventCallback(summarizeBySettings);

    const stableContextValue = useMemo(() => ({
        activeTreatmentId,
        clinicalNoteSettings,
        closeSummarySettingsModal,
        dockedRect,
        ensureRecorderStarted: ensureRecorderStartedStable,
        handleContinueRecording: handleContinueRecordingStable,
        handlePauseOrResumeRecording: handlePauseOrResumeRecordingStable,
        handleRestartRecording: handleRestartRecordingStable,
        handleStopRecording: handleStopRecordingStable,
        handleSummarizeAgain: handleSummarizeAgainStable,
        isRecorderVisible,
        isSummarySettingsModalEditMode: summarySettingsModalState.editMode,
        isSummarySettingsModalOpen: summarySettingsModalState.isOpen,
        latestSummaryResult,
        openSummarySettingsModal,
        recorderState,
        resetRecorder: resetRecorderStable,
        startFreshRecorderSession: startFreshRecorderSessionStable,
        summarizeBySettings: summarizeBySettingsStable,
        updateDockedRect,
    }), [
        activeTreatmentId,
        clinicalNoteSettings,
        closeSummarySettingsModal,
        dockedRect,
        ensureRecorderStartedStable,
        handleContinueRecordingStable,
        handlePauseOrResumeRecordingStable,
        handleRestartRecordingStable,
        handleStopRecordingStable,
        handleSummarizeAgainStable,
        isRecorderVisible,
        latestSummaryResult,
        openSummarySettingsModal,
        recorderState,
        resetRecorderStable,
        startFreshRecorderSessionStable,
        summarizeBySettingsStable,
        summarySettingsModalState.editMode,
        summarySettingsModalState.isOpen,
        updateDockedRect,
    ]);

    const liveContextValue = useMemo(() => ({
        audioIntensity,
        audioLevel,
        elapsedSeconds,
        recorderErrorMessage,
        recorderStatusMeta,
    }), [
        audioIntensity,
        audioLevel,
        elapsedSeconds,
        recorderErrorMessage,
        recorderStatusMeta,
    ]);

    return (
        <RecorderStableContext.Provider value={stableContextValue}>
            <RecorderLiveContext.Provider value={liveContextValue}>
                {children}
            </RecorderLiveContext.Provider>
        </RecorderStableContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRecorder() {
    const stable = useContext(RecorderStableContext);
    const live = useContext(RecorderLiveContext);
    return useMemo(() => ({ ...stable, ...live }), [live, stable]);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRecorderStable() {
    return useContext(RecorderStableContext);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRecorderLive() {
    return useContext(RecorderLiveContext);
}
